import {ApolloQueryResult, gql} from '@apollo/client';

import {updateAntibodyUIOpenVersionId} from 'antibodies/hooks/useAntibodyUIState';
import {AntibodyHistoryQuery} from 'antibodies/panels/__generated/AntibodyHistoryQuery';
import {ANTIBODY_HISTORY_QUERY} from 'antibodies/panels/AntibodyHistoryPanelView';
import {EventConstants} from 'constants/EventConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import AppDispatcher from 'lib/Dispatcher';
import notifications from 'notifications';

/**
 * Callback after creating a new version of an antibody after an update.
 * Opens the new version and updates the tags.
 */
export function onUpdateAntibodyVersion(
  antibodyId: string,
  newVersionId: string,
  tags: Array<SchemaTag>
): void {
  updateAntibodyUIOpenVersionId(antibodyId, newVersionId);
  notifications.success('Saved antibody');
  requeryAntibodyHistory(antibodyId);
  AppDispatcher.dispatch({
    actionType: EventConstants.FILE_TAGS_UPDATED,
    tagData: [{id: antibodyId, tags}],
  });
}

async function requeryAntibodyHistory(antibodyId: string): Promise<ApolloQueryResult<AntibodyHistoryQuery>> {
  return await getApolloClient().query({
    query: ANTIBODY_HISTORY_QUERY,
    variables: {antibodyId},
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
}

export const ANTIBODY_TAG_SCHEMA_QUERY = gql`
  query AntibodyTagSchemaQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    bioentity(id: $id) {
      ... on BasicFolderItemNode {
        id
        tagSchema {
          ... on TagSchemaNode {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ANTIBODY_SCHEMA_DOMAIN_LINKED_SCHEMAS_QUERY = gql`
  query AntibodySchemaDomainLinkedSchemasQuery($schemaId: StrictString!)
  @engComponent(name: MOLBIO)
  @skipAudit {
    implementedAntibodySchemaInterface(schemaId: $schemaId) {
      ... on ImplementedAntibodySchemaInterfaceNode {
        id
        domainLinkedSchemas {
          domainType
          specificity
          schemaOrInterfaceId
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
